import { createContext, useContext } from 'react';
import { API } from '@shared/types/api';

export interface CardNavigationItem {
  entityId: string;
  channelSlug: string;
  entityVariables?: API.ListWorkflowEntitiesQueryVariables;
  // explicit prev/next order, for a view whose rows aren't the entityVariables
  // query's own order (the tree flattens a hierarchy of separate level queries)
  entityIds?: string[];
  backTooltip?: string;
  /** The subtable open on this card, reopened when the stack comes back to it. */
  subtable?: CardNavigationSubtable;
}

/** A subtable's expanded modal, a row menu open over the field or the modal, or both. */
export interface CardNavigationSubtable {
  componentId: string;
  expanded: boolean;
  row?: CardNavigationSubtableRow;
}

/** The row menu's form: adding a row, or an existing row. */
export type CardNavigationSubtableRow = { type: 'new' } | { type: 'edit'; rowId: string };

export interface CardNavigationState {
  stack: CardNavigationItem[];
  currentItem?: CardNavigationItem;
  previousItem?: CardNavigationItem;
}

// Split from the state on purpose: the actions are stable for the provider's
// lifetime, while the stack changes on every card open. Consumers that only
// push onto the stack (every collection view group's open handler) would
// otherwise re-render on each stack change.
export interface CardNavigationActions {
  addStackItem: (item: CardNavigationItem) => void;
  /**
   * Pushes a card opened from inside another one (a related card), on top of the card it was
   * opened from, so back always returns there. A stack that doesn't end at that card is stale
   * (the card was opened directly, e.g. from a URL or search) and starts over from it.
   */
  addStackItemFrom: (origin: CardNavigationItem, item: CardNavigationItem) => void;
  updateCurrentItem: (target: CardNavigationItem) => void;
  removeCurrentItemAfterNavigation: () => void;
  resetStackAfterNavigation: () => void;
}

const defaultCardNavigationState: CardNavigationState = {
  stack: [],
  currentItem: undefined,
  previousItem: undefined,
};

const defaultCardNavigationActions: CardNavigationActions = {
  addStackItem: () => undefined,
  addStackItemFrom: () => undefined,
  updateCurrentItem: () => undefined,
  removeCurrentItemAfterNavigation: () => undefined,
  resetStackAfterNavigation: () => undefined,
};

export const CardNavigationStateContext = createContext<CardNavigationState>(
  defaultCardNavigationState,
);

export const CardNavigationActionsContext = createContext<CardNavigationActions>(
  defaultCardNavigationActions,
);

/** The card the update form has open, with its open subtable row; unset outside that form. */
export const CurrentCardNavigationItemContext = createContext<CardNavigationItem | undefined>(
  undefined,
);

export function useCardNavigationState(): CardNavigationState {
  return useContext(CardNavigationStateContext);
}

export function useCardNavigationActions(): CardNavigationActions {
  return useContext(CardNavigationActionsContext);
}
